import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BoltGlyph } from './glyphs.js';
import type { PhraseLessonView, PhraseLevelView } from './progression.js';

/** What the album receives: the levels to draw and what a tap does. */
export interface LookProps {
  levels: PhraseLevelView[];
  onOpen: (lesson: PhraseLessonView) => void;
}

/** `data-cefr` value of a level (`A1` → `a1`) — selects the level colour tokens. */
export function cefrAttr(level: PhraseLevelView): string {
  return level.code.toLowerCase();
}

/** Done = done or golden. */
export function isDone(lesson: PhraseLessonView): boolean {
  return lesson.state === 'done' || lesson.state === 'golden';
}

/** The ⚡ Pro mark shows where the learner decides: an open or next lesson
 *  they can't play without Pro. Locked lessons aren't reachable yet, and a
 *  finished one keeps its celebration look (a replay still meets the paywall). */
export function showsPro(lesson: PhraseLessonView): boolean {
  return lesson.proLocked && (lesson.state === 'open' || lesson.state === 'next');
}

/** The tile's spoken name: one sentence with its number, phrase, title and state. */
export function useTileLabel(): (lesson: PhraseLessonView) => string {
  const { t } = useTranslation();
  return useCallback(
    (lesson: PhraseLessonView) => {
      const name = lesson.tileEn ? `${lesson.tileEn} — ${lesson.title}` : lesson.title;
      const state = t(`phrases.state.${lesson.state}`);
      // Comma: two noun phrases ("შემდეგი გაკვეთილი, Pro გაკვეთილი") must not run together.
      const pro = showsPro(lesson) ? `, ${t('phrases.state.pro')}` : '';
      return t('phrases.tile_aria', { n: lesson.n, name, state: `${state}${pro}` });
    },
    [t],
  );
}

/** Attributes every tile carries — ONE interactive element per lesson.
 *  A progression-locked tile is disabled (no navigation); a Pro-locked one
 *  stays tappable and lands on the paywall at /learn/:id, like the main tree. */
export function tileAttrs(
  lesson: PhraseLessonView,
  label: string,
  onOpen: (lesson: PhraseLessonView) => void,
) {
  const locked = lesson.state === 'locked';
  const proLocked = showsPro(lesson);
  return {
    'data-state': lesson.state,
    'data-review': lesson.isReview || undefined,
    'data-pro-locked': proLocked || undefined,
    // The one gentle loop on the screen — not on a Pro-locked lesson (main-tree rule).
    'data-motion': (lesson.state === 'next' && !proLocked) || undefined,
    'data-phrases-next': lesson.state === 'next' || undefined,
    'data-testid': `phrase-lesson-${lesson.id}`,
    disabled: locked,
    'aria-label': label,
    onClick: () => onOpen(lesson),
  };
}

/** Small gold ⚡ badge on a lesson that needs Pro (the main tree's mark,
 *  drawn as SVG so it is the same crisp white bolt on every phone). */
export function ProBadge() {
  return (
    <span className="fluent-phrases__pro" aria-hidden="true">
      <BoltGlyph />
    </span>
  );
}

/** The green "დაწყება" plate inside the next lesson's tile. Not a button:
 *  the whole tile is the button. */
export function StartPlate({ className }: { className: string }) {
  const { t } = useTranslation();
  return (
    <span className={className} aria-hidden="true">
      {t('phrases.start')}
    </span>
  );
}
